// @ts-nocheck
import { uploadData } from "@aws-amplify/storage";


// Utility class to handle uploading images to S3 with customizable pathing and progress tracking
export interface UploadToS3Params {
  file: File;
  assetId?: string;
  organizationId?: string;
  target?: "asset" | "assetGroup" | "profile" | "organization" | "generic";
  uploadPathBuilder?: (args: {
    file: File;
    assetId?: string;
    identityId: string;
    organizationId?: string;
  }) => string;
  onProgress?: (percent: number) => void;
}

// Class providing static method to upload images to S3 with flexible options
export class UploadToS3 {
  static async uploadImage({
    file,
    assetId,
    organizationId,
    target,
    uploadPathBuilder,
    onProgress,
  }: UploadToS3Params): Promise<string> {
    if (!target && !uploadPathBuilder) {
      throw new Error("uploadTarget is required unless a custom uploadPathBuilder is provided");
    }

    // Without an org id the path below would fall through to the uploader's own protected folder
    if (target === "organization" && !organizationId) {
      throw new Error("organizationId is required to upload an organization picture");
    }

    // Perform the upload using Amplify's uploadData function
    const result = await uploadData({
      path: ({ identityId }) => {
        if (uploadPathBuilder) {
          return uploadPathBuilder({ file, assetId, identityId, organizationId });
        }

        // Built-in pathing based on upload target
        if (assetId && target === "assetGroup") {
          if (organizationId) {
            return `organizationprotected/${organizationId}/assetGroups/${assetId}/profilePic/${file.name}`;
          }
          return `protected/${identityId}/assetGroups/${assetId}/profilePic/${file.name}`;
        }

        // Org picture lives in the org's private folder, never under the uploader's own identity
        if (organizationId && target === "organization") {
          return `organizationprivate/${organizationId}/profilePic/${file.name}`;
        }

        if (target === "profile") {
          return `protected/${identityId}/profilePic/${file.name}`;
        }

        if (assetId && target === "asset") {
          if (organizationId) {
            return `organizationprotected/${organizationId}/assets/${assetId}/profilePic/${file.name}`;
          }
          return `protected/${identityId}/assets/${assetId}/profilePic/${file.name}`;
        }

        // Fallback generic path
        return `protected/${identityId}/uploads/${file.name}`;
      },
      data: file,
      options: {
        onProgress: ({ transferredBytes, totalBytes }) => {
          if (!onProgress || !totalBytes) return;
          const progress = Math.round((transferredBytes / totalBytes) * 100);
          onProgress(progress);
        },
      },
    });

    const uploadResult = await result.result;
    return uploadResult.path;
  }
}

export default UploadToS3;
